import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Alert, Loader } from 'rsuite';
import Stack from '@mui/material/Stack';

import {
  getCampaignElements,
  getCampaignLandingOrganizationId,
  updateCampaignElementValue,
} from '@src/redux/modules/campaignLandingPage';
import { getFileTypeLabels } from '@src/utils';
import { useFetchUploadPresignedUrl, useFileTypesData, useUploadToPresignedUrl } from '@src/hooks';
import styles from '@src/components/CampaignLanding/FileUploadInput.module.less';
import ElementDescription from '@src/components/CampaignLanding/ElementDescription';
import { VpDropzone, VpHoverInfo } from '@vp-ui';
import { getAcceptedMimeTypes } from '@src/utils/fileTypes';

const iconProps = {
  size: '16px',
};

const FileUploadInput = ({
  campaignElements,
  _updateCampaignElementValue,
  element,
  qid,
  organizationId,
}) => {
  const { isLoading, data: fileTypes } = useFileTypesData();
  const { mutateAsync: fetchUploadPresignedUrl } = useFetchUploadPresignedUrl();

  const { mutateAsync: uploadVideoToPresignedUrl } = useUploadToPresignedUrl();

  const allowedTypesLabel = getFileTypeLabels(element?.allowedValues, fileTypes?.data);

  const acceptMimeTypes = getAcceptedMimeTypes(element?.allowedValues, fileTypes?.data);

  const onRemove = () => {
    _updateCampaignElementValue({ qid, attachment: null });
  };

  const onDropRejected = (rejectedFiles) => {
    const failReason = rejectedFiles[0].errors[0].message;
    Alert.error(failReason, 7500);
    _updateCampaignElementValue({ qid, isUploading: false });
  };

  const acceptedFiles = `Accepted File Types: ${
    isLoading ? <Loader className={styles.vpFileTypesLoader} size="xs" /> : `${allowedTypesLabel}`
  }`;

  const onDropAccepted = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    try {
      _updateCampaignElementValue({ qid, isUploading: true });

      const fileExtension = file.name.split('.').pop();

      const res = await fetchUploadPresignedUrl({
        mimeType: file.type,
        extension: fileExtension,
        asset_type: 'attachment',
        organizationId,
      });

      const { presignedUrl } = res;

      const url = new URL(presignedUrl);
      const filePath = url.pathname.slice(1);

      await uploadVideoToPresignedUrl({ presignedUrl, file });

      const attachment = {
        id: crypto.randomUUID(),
        filename: filePath.split('/')[1].split('.')[0],
        extension: fileExtension,
        path: filePath,
        size: file?.size,
        originalFilename: file?.name,
        contentType: file?.type,
      };

      _updateCampaignElementValue({ qid, attachment, isUploading: false });
    } catch (error) {
      // @TODO show error properly
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <section className={styles.vpFileUploadInputContainer}>
      <div className={styles.vpFileUploadInputLabelContainer}>
        <Stack direction="row" alignItems="center" gap="5px">
          <strong>
            {element?.label}
            {element?.required && <span className={styles.vpRequiredLabel}>*</span>}
          </strong>
          <VpHoverInfo label={acceptedFiles} icon="Info" iconProps={iconProps} />
        </Stack>
      </div>
      <ElementDescription description={element?.description} />
      <VpDropzone
        accept={acceptMimeTypes}
        title="Click or drag a file to this area to upload."
        onDropAccepted={onDropAccepted}
        onDropRejected={onDropRejected}
        multiple={false}
        maxFiles={1}
        maxSize={500 * 1e6} // 500MB
        onRemove={onRemove}
        hasFile={!!campaignElements[qid].attachment}
        disabled={campaignElements[qid]?.isUploading}
        loading={campaignElements[qid]?.isUploading}
      />
    </section>
  );
};

FileUploadInput.propTypes = {
  _updateCampaignElementValue: PropTypes.func,
  element: PropTypes.object,
  qid: PropTypes.string,
  organizationId: PropTypes.string,
  campaignElements: PropTypes.object,
};

const mapStateToProps = (state) => ({
  campaignElements: getCampaignElements(state),
  organizationId: getCampaignLandingOrganizationId(state),
});

const mapDispatchToProps = {
  _updateCampaignElementValue: updateCampaignElementValue,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileUploadInput);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/FileUploadInput.jsx